import React, { useMemo } from 'react';
import styles from './bookedCard.module.css';
import { ReactSVG } from 'react-svg';
import { Icons, TerminalsIcon } from '@/assets';
import { Icon } from '@/components/Icon';
import TooltipV2 from '@/components/Tooltip';
import timeAgo from '@/ctUtlis/timeAgo';
import IconButton from '@/ctComponents/IconButton';
import CustomCheckbox from '@/components/CustomCheckbox';

const Card = ({
  terminal,
  date,
  time,
  license_plate,
  type,
  status,
  statusText,
  width,
  terminalId,
  lastCheckedAt,
  fromAutobook,
  lastMessage,
  tooltipSuffix,
  data,
  history,
  loadingHistory,
  showHistory,
  setShow,
  fromModal,
  originSource,
  handleDispatchConfirm,
  checkedDispatch,
  isReadOnly = false,
}) => {
  const safe = (v) => (v || '').toString().replace(/\s+/g, '_');
  const tooltipId = `booked-${safe(terminalId)}-${safe(date)}-${safe(
    time
  )}-${safe(license_plate)}-${safe(tooltipSuffix)}`;
  const hoveredIconKey = tooltipId;

  const getOriginSourceClass = (originSource) => {
    switch (originSource) {
      case 'AUTOBOOK':
        return 'text-success-secondary font-bold border border-solid border-success-secondary';
      case 'MANUAL':
        return 'text-primary-blue border font-bold  border-solid border-primary-blue';
      default:
        return 'text-inactive-text font-bold border border-solid border-inactive-text ';
    }
  };

  const getOriginSourceText = (originSource) => {
    switch (originSource) {
      case 'AUTOBOOK':
        return 'Autobook';
      case 'MANUAL':
        return 'Manual';
      default:
        return 'External';
    }
  };

  const getBackgroundColor = useMemo(
    () => (status) => {
      return status === 'EDITED'
        ? '#335CFF'
        : status === 'RESERVED'
        ? '#1FC16B'
        : status === 'CANCELED' || status === 'REJECTED'
        ? '#FFEBEC'
        : '#99A0AE';
    },
    []
  );

  const showDispatch = false;
  // status === 'RESERVED' || status === 'EDITED' || status === 'USED';

  return (
    <div
      className={styles.card}
      style={{
        width: width,
        border: fromAutobook ? '1px solid #1fc16b' : 'none',
      }}>
      {!isReadOnly && (
        <div
          className={styles.status}
          style={{
            backgroundColor: getBackgroundColor(status),
            color:
              status === 'EDITED'
                ? '#EBF1FF'
                : status === 'CANCELED' || status === 'REJECTED'
                ? '#D02533'
                : 'white',
            paddingRight: showDispatch ? '4px' : '12px',
          }}>
          {lastMessage && (
            <TooltipV2
              tooltipId={tooltipId}
              iconId={hoveredIconKey}
              arrow
              title={lastMessage}
              style={{}}
              placement="top">
              <Icon
                width="20px"
                height="20px"
                src={Icons.infoCircle}
                color={
                  status === 'EDITED'
                    ? '#335CFF'
                    : status === 'RESERVED'
                    ? '#1FC16B'
                    : status === 'CANCELED' || status === 'REJECTED'
                    ? '#FB3748'
                    : '#99A0AE'
                }
              />
            </TooltipV2>
          )}
          <span
            className={
              statusText?.toLowerCase() === 'scheduling' ||
              statusText?.toLowerCase() === 'rescheduling' ||
              statusText?.toLowerCase() === 'canceling'
                ? styles.loadingText
                : ''
            }>
            {statusText}
          </span>
          {showDispatch && (
            <div
              style={{
                color: getBackgroundColor(status),
              }}
              className="h-5 px-1 bg-white gap-1 rounded-full text-9 font-bold flex items-center justify-between">
              Driver Dispatched
              <span className="w-4">
                <CustomCheckbox
                  checked={checkedDispatch}
                  onChange={(e) => handleDispatchConfirm(e.target.checked)}
                  style={{ width: '12px', height: '12px' }}
                />
              </span>
            </div>
          )}
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.logoSection}>
          <div className={styles.terminalCard}>
            <a
              className={styles.terminalLink}
              target="_blank"
              rel="noopener noreferrer"
              href={data[0]?.links?.gate_schedule || '#'}>
              <img
                width={100}
                height={30}
                src={TerminalsIcon[terminal]}
                alt={terminal + ' terminal logo'}
              />
            </a>
          </div>
          <span className="text-icon-gray text-8 text-center whitespace-nowrap">
            {lastCheckedAt && `Checked: ${timeAgo(lastCheckedAt)}`}
          </span>
          {originSource && (
            <div
              className={`px-3 h-5 flex items-center justify-center text-10 mt-2 rounded-full ${getOriginSourceClass(
                originSource
              )}`}>
              {getOriginSourceText(originSource)}
            </div>
          )}
        </div>
        <div className={styles.verticalDashed} />
        <div className={styles.detailContainer}>
          <div className={styles.details}>
            <div className={styles.detailItem}>
              <ReactSVG src={Icons.calendarday} className={styles.icon} />
              Date:
              <span className={styles.boldText}>{date}</span>
            </div>
            <div className={styles.detailItem}>
              <ReactSVG src={Icons.calendartime} className={styles.icon} />
              Time:
              <span className={styles.boldText}>{time}</span>
            </div>
          </div>
          <div className={styles.details}>
            <div className={styles.detailItem}>
              <ReactSVG src={Icons.licensePlate} className={styles.icon} />
              License Plate:
              <span className={styles.boldText}>
                {license_plate?.slice(0, 7)}
              </span>
            </div>
            <div className={styles.detailItem}>
              <ReactSVG
                src={data?.type === 2 ? Icons.DualShift : Icons.SingleShift}
                className={styles.icontype}
              />
              Type:
              <span className={styles.boldText}>{type}</span>
            </div>
          </div>
        </div>
        {!(history?.length > 0) && !fromModal && !isReadOnly && (
          <>
            <div className={styles.verticalDashed} />
            <div className={styles.actions}>
              <IconButton
                disable={data.status === 'PENDING_RESERVE' ? true : false}
                onClick={showHistory}
                // icon={'history'}
                text={'History'}
                color={'#131517'}
                loading={loadingHistory}
              />
              <IconButton
                disable={
                  data[0]?.status === 'EDITED' || data[0]?.status === 'RESERVED'
                    ? false
                    : true
                }
                onClick={() => setShow(true)}
                // icon={'redX'}
                text={'Cancel'}
                color={'#FB3748'}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Card;
